/**
 * Tutorials Page - Frick & Frack's Guide
 * =======================================
 * Learn coding concepts from Frick & Frack!
 * Interactive tutorials for young hackers.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  Container, 
  Activity,
  Brain,
  LogOut,
  Flame,
  Terminal,
  Star,
  Settings,
  BookOpen,
  Play,
  CheckCircle,
  Lock,
  ChevronRight,
  Code2,
  Database,
  Server,
  Zap,
  Rocket,
  HelpCircle
} from "lucide-react";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "FRICK & FRACK GUIDES", icon: <Star className="w-5 h-5" />, path: "/tutorials" },
    { id: "settings", label: "SETTINGS", icon: <Settings className="w-5 h-5" />, path: "/settings" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Tutorial Card Component
const TutorialCard = ({ tutorial, onClick }) => {
  const statusColors = {
    completed: "border-green-500/30 bg-green-500/5",
    in_progress: "border-primary/30 bg-primary/5",
    locked: "border-white/5 bg-black/20 opacity-60",
    available: "border-white/10 hover:border-primary/50"
  };

  const statusIcons = {
    completed: <CheckCircle className="w-5 h-5 text-green-400" />,
    in_progress: <Play className="w-5 h-5 text-primary" />,
    locked: <Lock className="w-5 h-5 text-muted-foreground" />,
    available: <BookOpen className="w-5 h-5 text-blue-400" />
  };

  return (
    <button
      onClick={onClick}
      disabled={tutorial.status === 'locked'}
      className={`w-full p-6 border text-left transition-all ${statusColors[tutorial.status]} ${tutorial.status !== 'locked' ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      data-testid={`tutorial-${tutorial.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 flex items-center justify-center ${tutorial.status === 'locked' ? 'bg-white/5' : 'bg-black/30'}`}>
          {tutorial.icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-white">{tutorial.title}</h3>
            {statusIcons[tutorial.status]}
          </div>
          <p className="text-sm text-muted-foreground font-mono mb-3">
            {tutorial.description}
          </p>
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="text-muted-foreground">
              {tutorial.duration} min
            </span>
            <span className="text-primary">
              +{tutorial.xp} XP
            </span>
            <span className={`px-2 py-0.5 rounded ${
              tutorial.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
              tutorial.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {tutorial.difficulty}
            </span>
          </div>
          {tutorial.status === 'in_progress' && (
            <Progress value={tutorial.progress} className="h-1 mt-3 bg-muted" />
          )}
        </div>
      </div>
    </button>
  );
};

// Tutorial Content Modal
const TutorialContent = ({ tutorial, onClose }) => {
  const [step, setStep] = useState(0);

  const tutorialSteps = {
    'docker-basics': [
      {
        title: "What is Docker?",
        content: `**Frick says:** "Docker is like a lunchbox for your code!"

Imagine you made a really cool sandwich at home. But when you bring it to school, it gets all squished and messy.

Docker is like a special container that keeps your "code sandwich" exactly the way you made it!

**Key Concept:** A container keeps your code safe and working the same way everywhere.`,
        code: null
      },
      {
        title: "Your First Container",
        content: `**Frack says:** "Let's deploy something!"

When you click "New Deployment" on CodeForge, you're creating a container. It's like pressing a button on a vending machine - your website pops out ready to go!

Inside your container:
- Debian 12 (the operating system)
- PHP 8.2 (the language)
- Your code files`,
        code: `// Your container runs this automatically!
<?php
echo "Hello from my container!";
?>`
      },
      {
        title: "Container Commands",
        content: `**Frick & Frack together:** "Control your container like a video game!"

- **START** - Wake up your container (like turning on a game console)
- **STOP** - Put it to sleep (saves resources)
- **RESTART** - Turn it off and on again (fixes most problems!)

Each action earns you **+25 XP!**`,
        code: null
      }
    ],
    'php-intro': [
      {
        title: "What is PHP?",
        content: `**Frick says:** "PHP is the language of the web!"

PHP stands for "PHP: Hypertext Preprocessor" (yes, it's a funny name).

It's a programming language that runs on servers and creates web pages. Facebook was built with PHP!

PHP code always starts with \`<?php\` and ends with \`?>\``,
        code: `<?php
// This is a PHP comment!
// Comments help explain your code

echo "Hello, World!";
// "echo" prints text to the screen
?>`
      },
      {
        title: "Variables",
        content: `**Frack says:** "Variables are like labeled boxes!"

A variable stores information. Think of it like a box with a name tag.

In PHP, variables start with a \`$\` sign.`,
        code: `<?php
$name = "Frick";      // A text variable
$age = 8;             // A number variable
$is_hacker = true;    // A true/false variable

echo "My name is " . $name;
echo "I am " . $age . " years old";
?>`
      },
      {
        title: "If Statements",
        content: `**Frick says:** "Computers need to make decisions too!"

An IF statement checks if something is true, then does something.

It's like: "IF it's raining, THEN bring an umbrella"`,
        code: `<?php
$level = 5;

if ($level >= 5) {
    echo "You're a Rising Star!";
} else {
    echo "Keep leveling up!";
}

// This will print: "You're a Rising Star!"
?>`
      }
    ],
    'ml-basics': [
      {
        title: "What is Machine Learning?",
        content: `**Frack says:** "Teaching robots to learn patterns!"

Machine Learning is when we teach computers to learn from examples, instead of giving them exact rules.

It's like showing a robot 1000 pictures of cats until it can recognize any cat!

**Key Types:**
- **Regression** - Predict numbers (like prices)
- **Classification** - Predict categories (like "spam" or "not spam")`,
        code: null
      },
      {
        title: "Training a Model",
        content: `**Frick says:** "More data = smarter robot!"

To train a model, you need:
1. **Data** - Examples to learn from
2. **Algorithm** - The learning method (we use XGBoost)
3. **Target** - What you want to predict

The model looks at your data and finds patterns!`,
        code: `-- This is SQL (database language)
-- PostgresML makes it easy!

SELECT * FROM pgml.train(
    'my_model',           -- Model name
    'regression',         -- What type
    'training_data',      -- Your data table
    'price'               -- What to predict
);`
      },
      {
        title: "Making Predictions",
        content: `**Frack says:** "Now the robot can guess!"

Once your model is trained, you can ask it to predict new things.

You give it features (input), and it gives you a prediction (output).

**Accuracy** tells you how often the model is right. 95% accuracy means it's right 95 times out of 100!`,
        code: `-- Ask the model to predict
SELECT pgml.predict(
    'my_model',
    ARRAY[feature1, feature2, feature3]
) as prediction;

-- The robot returns its best guess!`
      }
    ]
  };

  const steps = tutorialSteps[tutorial.id] || [];
  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#16191F] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="border-b border-white/5 p-4 flex items-center justify-between sticky top-0 bg-[#16191F]">
          <div className="flex items-center gap-3">
            {tutorial.icon}
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {tutorial.title}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-white">
            ✕
          </button>
        </div>

        {/* Progress */}
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-mono text-muted-foreground">
              Step {step + 1} of {steps.length}
            </span>
          </div>
          <Progress value={((step + 1) / steps.length) * 100} className="h-1 bg-muted" />
        </div>

        {/* Content */}
        {currentStep && (
          <div className="p-6">
            <h3 className="text-lg font-bold text-primary mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {currentStep.title}
            </h3>
            <div className="text-muted-foreground font-mono text-sm whitespace-pre-wrap mb-6">
              {currentStep.content.split('**').map((part, i) => 
                i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
              )}
            </div>
            {currentStep.code && (
              <div className="bg-[#0D1117] border border-white/5 p-4 font-mono text-sm">
                <pre className="text-green-400 whitespace-pre-wrap">{currentStep.code}</pre>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="p-4 border-t border-white/5 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className="border-white/10 font-mono"
          >
            Previous
          </Button>
          {step === steps.length - 1 ? (
            <Button
              onClick={onClose}
              className="bg-green-500 text-black font-bold"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete (+{tutorial.xp} XP)
            </Button>
          ) : (
            <Button
              onClick={() => setStep(s => s + 1)}
              className="bg-primary text-black font-bold"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const TutorialsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedTutorial, setSelectedTutorial] = useState(null);

  const tutorials = [
    {
      id: 'docker-basics',
      title: "Docker Basics",
      description: "Learn what containers are and how to deploy your first one",
      icon: <Container className="w-6 h-6 text-blue-400" />,
      duration: 5,
      xp: 30,
      difficulty: "Beginner",
      status: "available"
    },
    {
      id: 'php-intro',
      title: "PHP for Beginners",
      description: "Write your first PHP code and understand the basics",
      icon: <Code2 className="w-6 h-6 text-purple-400" />,
      duration: 10,
      xp: 50,
      difficulty: "Beginner",
      status: "available"
    },
    {
      id: 'ml-basics',
      title: "Machine Learning 101",
      description: "Understand how robots learn from data",
      icon: <Brain className="w-6 h-6 text-green-400" />,
      duration: 8,
      xp: 40,
      difficulty: "Beginner",
      status: "available"
    },
    {
      id: 'databases',
      title: "PostgreSQL Fundamentals",
      description: "Store and retrieve data like a pro",
      icon: <Database className="w-6 h-6 text-cyan-400" />,
      duration: 12,
      xp: 60,
      difficulty: "Intermediate",
      status: "available"
    },
    {
      id: 'advanced-docker',
      title: "Advanced Containers",
      description: "Scale your apps and manage multiple containers",
      icon: <Server className="w-6 h-6 text-orange-400" />,
      duration: 15,
      xp: 80,
      difficulty: "Intermediate",
      status: (user?.level || 1) >= 3 ? "available" : "locked"
    },
    {
      id: 'api-design',
      title: "Building APIs",
      description: "Create endpoints that other programs can talk to",
      icon: <Zap className="w-6 h-6 text-yellow-400" />,
      duration: 20,
      xp: 100,
      difficulty: "Advanced",
      status: (user?.level || 1) >= 5 ? "available" : "locked"
    },
  ];

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="tutorials" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4">
            <h1 
              className="text-2xl font-bold text-white uppercase tracking-tight flex items-center gap-3"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="tutorials-title"
            >
              <BookOpen className="w-7 h-7 text-primary" />
              FRICK & FRACK'S GUIDE
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-1">
              Learn coding concepts the fun way!
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-primary/10 via-blue-500/10 to-purple-500/10 border border-primary/20 p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/20">
                <HelpCircle className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  WELCOME TO THE GUIDES!
                </h3>
                <p className="text-muted-foreground font-mono text-sm mb-4">
                  We're Frick & Frack, and we're here to teach you coding! 
                  We learned everything here, and now it's your turn.
                  Complete tutorials to earn XP and unlock new ones!
                </p>
                <div className="flex items-center gap-4 text-sm font-mono">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-primary rounded-full" />
                    <span className="text-primary">Frick (The Architect)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full" />
                    <span className="text-green-400">Frack (The Operator)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tutorial Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {tutorials.map((tutorial) => (
              <TutorialCard 
                key={tutorial.id}
                tutorial={tutorial}
                onClick={() => tutorial.status !== 'locked' && setSelectedTutorial(tutorial)}
              />
            ))}
          </div>
        </main>
      </div>

      {/* Tutorial Content Modal */}
      {selectedTutorial && (
        <TutorialContent 
          tutorial={selectedTutorial}
          onClose={() => setSelectedTutorial(null)}
        />
      )}
    </div>
  );
};

export default TutorialsPage;
